import { PageHeader, FilterBar, LoadingState, DataTable } from '../components/AdminPageUI';
import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Button, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Grid, CircularProgress, TablePagination } from '@mui/material';
import { Search as SearchIcon, Print as PrintIcon, Edit as EditIcon, AutoFixHigh as AutoFixHighIcon } from '@mui/icons-material';
import api from '../utils/Api';
import { toast } from 'react-toastify';

export default function SchoolAdmissionRegisterPage() {
  const [students, setStudents] = useState([]);

  const rawUser = localStorage.getItem('user');
  const storedSchoolName = rawUser ? (JSON.parse(rawUser).school?.name || 'School') : 'School';
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalCount, setTotalCount] = useState(0);

  const [isExporting, setIsExporting] = useState(false);

  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generatePattern, setGeneratePattern] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateProgress, setGenerateProgress] = useState(null);
  const [studentsToGenerate, setStudentsToGenerate] = useState([]);

  const initialLoadDone = React.useRef(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchStudents = React.useCallback(async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);
      const { data } = await api.get('/students');
      const studentData = Array.isArray(data) ? data : [];
      setStudents(studentData);
      setTotalCount(studentData.length);
    } catch (err) {
      console.error('Failed to fetch students', err);
      toast.error('Failed to load register');
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleOpenGenerateDialog = () => {
    const needing = students.filter(s => !s.admissionNumber || String(s.admissionNumber).trim() === '');
    if (needing.length === 0) {
      toast.info('All students already have an admission number!');
      return;
    }
    setStudentsToGenerate(needing);

    let bestGuess = 'STU-0001';
    let maxNum = 0;
    let foundPrefix = 'STU-';
    let foundSuffix = '';
    let foundPad = 4;

    const hasNumber = students.filter(s => s.admissionNumber && String(s.admissionNumber).trim() !== '');
    if (hasNumber.length > 0) {
      for (const s of hasNumber) {
        const match = String(s.admissionNumber).match(/^(.*?)(\d+)(.*?)$/);
        if (match) {
          const num = parseInt(match[2], 10);
          if (num > maxNum) {
            maxNum = num;
            foundPrefix = match[1];
            foundSuffix = match[3];
            foundPad = match[2].length;
          }
        }
      }
      if (maxNum > 0) {
        const nextNum = maxNum + 1;
        bestGuess = `${foundPrefix}${String(nextNum).padStart(foundPad, '0')}${foundSuffix}`;
      }
    }

    setGeneratePattern(bestGuess);
    setGenerateDialogOpen(true);
  };

  const handleGenerateNumbers = async () => {
    if (!generatePattern.trim()) {
      toast.error('Please enter a valid starting pattern');
      return;
    }

    const match = generatePattern.match(/^(.*?)(\d+)(.*?)$/);
    if (!match) {
      toast.error('Pattern must contain at least one number sequence (e.g. STU-0001)');
      return;
    }

    const prefix = match[1];
    let currentNum = parseInt(match[2], 10);
    const padLength = match[2].length;
    const suffix = match[3];

    setIsGenerating(true);
    setGenerateProgress({ current: 0, total: studentsToGenerate.length });

    try {
      const existingNumbers = new Set(students.map(s => s.admissionNumber).filter(Boolean));
      const chunkSize = 5;
      
      for (let i = 0; i < studentsToGenerate.length; i += chunkSize) {
        const chunk = studentsToGenerate.slice(i, i + chunkSize);
        
        await Promise.all(chunk.map((student) => {
          let newNumber;
          do {
            newNumber = `${prefix}${String(currentNum++).padStart(padLength, '0')}${suffix}`;
          } while (existingNumbers.has(newNumber));
          
          existingNumbers.add(newNumber);
          return api.put(`/students/${student._id}`, { admissionNumber: newNumber });
        }));

        setGenerateProgress({ current: Math.min(i + chunkSize, studentsToGenerate.length), total: studentsToGenerate.length });
      }

      toast.success(`Successfully generated ${studentsToGenerate.length} admission numbers!`);
      setGenerateDialogOpen(false);
      fetchStudents();
    } catch (err) {
      console.error(err);
      toast.error('An error occurred while generating admission numbers');
    } finally {
      setIsGenerating(false);
      setGenerateProgress(null);
    }
  };

  const handlePrint = () => {
    try {
      setIsExporting(true);

      // A4 landscape gives ~297mm × 210mm usable area. Using A4 ensures consistent printing scaling across mobile and desktop.
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

      // ── Header ────────────────────────────────────────────────────────────
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(storedSchoolName.toUpperCase(), doc.internal.pageSize.getWidth() / 2, 14, { align: 'center' });

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('REGISTER OF ADMISSION AND WITHDRAWAL', doc.internal.pageSize.getWidth() / 2, 21, { align: 'center' });

      // ── Table ─────────────────────────────────────────────────────────────
      // Build head
      const head = [
        [
          { content: 'Adm. No.', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
          { content: 'Date of Admission / Re-admission', colSpan: 3, styles: { halign: 'center' } },
          { content: 'NAME (in full)', colSpan: 2, styles: { halign: 'center' } },
          { content: 'Date of Birth', colSpan: 3, styles: { halign: 'center' } },
          { content: 'Guardian', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
          { content: 'Address', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
          { content: 'Last School Attended', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
          { content: 'Date of Leaving', colSpan: 3, styles: { halign: 'center' } },
          { content: 'Cause for Leaving', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
          { content: 'Remarks', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
        ],
        ['D', 'M', 'Y', 'Surname', 'Other Name(s)', 'D', 'M', 'Y', 'D', 'M', 'Y'],
      ];

      // Build body from ALL filtered students (not just current page)
      const body = filteredStudents.map(s => [
        s.admissionNumber || '',
        s.admDate.d, s.admDate.m, s.admDate.y,
        (s.derivedSurname || '').toUpperCase(),
        (s.derivedOtherNames || '').toUpperCase(),
        s.dobDate.d, s.dobDate.m, s.dobDate.y,
        s.guardianName || s.fatherName || s.motherName || '',
        s.address || '',
        s.lastSchoolAttended || '',
        s.leaveDate.d, s.leaveDate.m, s.leaveDate.y,
        s.causeForLeaving || '',
        s.remarks || '',
      ]);

      autoTable(doc, {
        startY: 26,
        head,
        body,
        theme: 'grid',
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 6,
          halign: 'center',
          valign: 'middle',
          cellPadding: 1.5,
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        bodyStyles: {
          fontSize: 6,
          cellPadding: 1.5,
          valign: 'middle',
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        columnStyles: {
          0:  { halign: 'center', cellWidth: 16 },   // Adm No
          1:  { halign: 'center', cellWidth: 6 },    // D
          2:  { halign: 'center', cellWidth: 6 },    // M
          3:  { halign: 'center', cellWidth: 8 },    // Y
          4:  { cellWidth: 28 },                     // Surname
          5:  { cellWidth: 32 },                     // Other Names
          6:  { halign: 'center', cellWidth: 6 },    // D
          7:  { halign: 'center', cellWidth: 6 },    // M
          8:  { halign: 'center', cellWidth: 8 },    // Y
          9:  { cellWidth: 30 },                     // Guardian
          10: { cellWidth: 32 },                     // Address
          11: { cellWidth: 30 },                     // Last School
          12: { halign: 'center', cellWidth: 6 },    // D
          13: { halign: 'center', cellWidth: 6 },    // M
          14: { halign: 'center', cellWidth: 8 },    // Y
          15: { cellWidth: 30 },                     // Cause
          16: { cellWidth: 29 },                     // Remarks
        },
        margin: { left: 5, right: 5 },
        didDrawPage: (data) => {
          // Footer with page number
          const pageCount = doc.internal.getNumberOfPages();
          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();
          doc.setFontSize(7);
          doc.setTextColor(120);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            pageWidth / 2, pageHeight - 5,
            { align: 'center' }
          );
        },
      });

      const safeSchoolName = storedSchoolName.replace(/[^a-zA-Z0-9_\- ]/g, '').trim();
      
      if (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
        // iOS Safari sometimes struggles to download via doc.save, and native print dialog prefers to open it directly
        const pdfUrl = doc.output('bloburl');
        window.open(pdfUrl, '_blank');
      } else {
        doc.save(`${safeSchoolName}_Admission_Register.pdf`);
      }
      toast.success('Admission Register PDF generated successfully');
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const openEditDialog = (student) => {
    setEditingStudent(student);

    // Derive surname/othernames if missing
    let defaultSurname = student.surname || '';
    let defaultOther = student.otherNames || '';

    if (!defaultSurname && !defaultOther && student.user?.name) {
      const parts = student.user.name.trim().split(' ');
      defaultSurname = parts.shift();
      defaultOther = parts.join(' ');
    }

    setFormData({
      admissionNumber: student.admissionNumber || '',
      surname: defaultSurname,
      otherNames: defaultOther,
      dateOfAdmission: student.dateOfAdmission ? student.dateOfAdmission.substring(0, 10) : (student.createdAt ? student.createdAt.substring(0, 10) : ''),
      lastSchoolAttended: student.lastSchoolAttended || '',
      dateOfLeaving: student.dateOfLeaving ? student.dateOfLeaving.substring(0, 10) : '',
      causeForLeaving: student.causeForLeaving || '',
      remarks: student.remarks || '',
    });
  };

  const closeEditDialog = () => {
    setEditingStudent(null);
    setFormData({});
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Combine surname and otherNames to update the global User name
      const payload = {
        ...formData,
        name: `${formData.surname || ''} ${formData.otherNames || ''}`.trim()
      };
      
      await api.put(`/students/${editingStudent._id}`, payload);
      toast.success('Register entry updated');
      fetchStudents();
      closeEditDialog();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save register entry');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Helper to safely format dates into D, M, Y
  const getDMY = (dateStr) => {
    if (!dateStr) return { d: '', m: '', y: '' };
    const d = new Date(dateStr);
    if (isNaN(d.valueOf())) return { d: '', m: '', y: '' };
    return {
      d: String(d.getDate()).padStart(2, '0'),
      m: String(d.getMonth() + 1).padStart(2, '0'),
      y: String(d.getFullYear()).slice(-2) // Last two digits of year
    };
  };

  const derivedStudents = students.map(s => {
    const pDateAdmit = getDMY(s.dateOfAdmission || s.createdAt || s.user?.createdAt);
    const pDob = getDMY(s.dateOfBirth);
    const pDateLeave = getDMY(s.dateOfLeaving);

    let sn = s.surname;
    let on = s.otherNames;
    if (!sn && !on && s.user?.name) {
      const parts = s.user.name.trim().split(' ');
      sn = parts.shift();
      on = parts.join(' ');
    }

    return {
      ...s,
      derivedSurname: sn || '',
      derivedOtherNames: on || '',
      admDate: pDateAdmit,
      dobDate: pDob,
      leaveDate: pDateLeave
    };
  }).sort((a, b) => {
    const nameA = ((a.derivedSurname || '') + ' ' + (a.derivedOtherNames || '')).trim().toLowerCase();
    const nameB = ((b.derivedSurname || '') + ' ' + (b.derivedOtherNames || '')).trim().toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const filteredStudents = derivedStudents.filter(s => {
    const q = debouncedSearch.toLowerCase();
    return (s.user?.name || '').toLowerCase().includes(q) ||
      (s.admissionNumber || '').toLowerCase().includes(q);
  });

  const displayedStudents = filteredStudents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Update total count when search changes so pagination scale adjusts
  useEffect(() => {
    setTotalCount(filteredStudents.length);
  }, [debouncedSearch, students]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ pb: 10 }}>
      {/* Header controls - Hidden when printing */}
      <PageHeader className="no-print" title={<>School Admission Register</>} description={<>Search student admissions, generate admission numbers, and print the register.</>} actions={<>
        <Button variant="contained" color="secondary" startIcon={<AutoFixHighIcon />} onClick={handleOpenGenerateDialog} className="no-print">Generate Admission No.</Button>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} disabled={isExporting}>{isExporting ? 'Generating PDF...' : 'Print Register'}</Button>
      </>}></PageHeader>
      <FilterBar>
<TextField
            size="small"
            placeholder="Search Admission No. or Name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
          />
      </FilterBar>

      {loading ? (
        <LoadingState />
      ) : (
        <>

          <DataTable component={Paper} sx={{
            boxShadow: 2,
            borderRadius: 2,
            '@media print': { boxShadow: 'none', borderRadius: 0 }
          }}>
            <Table size="small" sx={{
              minWidth: 1500,
              '& th, & td': { border: '1px solid #e0e0e0', px: 1, py: 0.5 },
              '& th': { fontWeight: 'bold', bgcolor: '#fafafa', textAlign: 'center' }
            }}>
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={{ width: 80 }}>Admission<br />Number</TableCell>
                  <TableCell colSpan={3}>Date of Admission or<br />Re-admission</TableCell>
                  <TableCell colSpan={2}>NAME (in full)</TableCell>
                  <TableCell colSpan={3}>Exact Date of Birth</TableCell>
                  <TableCell rowSpan={2}>Name of<br />Guardian</TableCell>
                  <TableCell rowSpan={2} sx={{ minWidth: 150 }}>Address</TableCell>
                  <TableCell rowSpan={2} sx={{ minWidth: 150 }}>Last School<br />Attended</TableCell>
                  <TableCell colSpan={3}>Date of attendance at<br />this School</TableCell>
                  <TableCell rowSpan={2} sx={{ minWidth: 120 }}>Cause for Leaving</TableCell>
                  <TableCell rowSpan={2} sx={{ minWidth: 120 }}>REMARKS</TableCell>
                  <TableCell rowSpan={2} className="no-print">Actions</TableCell>
                </TableRow>
                <TableRow>
                  {/* Date of Admission */}
                  <TableCell sx={{ minWidth: 30 }}>D</TableCell>
                  <TableCell sx={{ minWidth: 30 }}>M</TableCell>
                  <TableCell sx={{ minWidth: 30 }}>Y</TableCell>
                  {/* Name */}
                  <TableCell sx={{ minWidth: 120 }}>Surname</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Other Name(s)</TableCell>
                  {/* DOB */}
                  <TableCell sx={{ minWidth: 30 }}>D</TableCell>
                  <TableCell sx={{ minWidth: 30 }}>M</TableCell>
                  <TableCell sx={{ minWidth: 30 }}>Y</TableCell>
                  {/* Date of leaving */}
                  <TableCell sx={{ minWidth: 30 }}>D</TableCell>
                  <TableCell sx={{ minWidth: 30 }}>M</TableCell>
                  <TableCell sx={{ minWidth: 30 }}>Y</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedStudents.map((s) => (
                  <TableRow key={s._id} hover>
                    <TableCell align="center">{s.admissionNumber}</TableCell>

                    {/* Adm Date */}
                    <TableCell align="center">{s.admDate.d}</TableCell>
                    <TableCell align="center">{s.admDate.m}</TableCell>
                    <TableCell align="center">{s.admDate.y}</TableCell>

                    {/* Name */}
                    <TableCell sx={{ textTransform: 'uppercase' }}>{s.derivedSurname}</TableCell>
                    <TableCell sx={{ textTransform: 'uppercase' }}>{s.derivedOtherNames}</TableCell>

                    {/* DOB */}
                    <TableCell align="center">{s.dobDate.d}</TableCell>
                    <TableCell align="center">{s.dobDate.m}</TableCell>
                    <TableCell align="center">{s.dobDate.y}</TableCell>

                    {/* Guardian */}
                    <TableCell>{s.guardianName || s.fatherName || s.motherName}</TableCell>

                    {/* Address */}
                    <TableCell>{s.address}</TableCell>

                    {/* Last School */}
                    <TableCell>{s.lastSchoolAttended}</TableCell>

                    {/* Leave Date */}
                    <TableCell align="center">{s.leaveDate.d}</TableCell>
                    <TableCell align="center">{s.leaveDate.m}</TableCell>
                    <TableCell align="center">{s.leaveDate.y}</TableCell>

                    {/* Cause & Remarks */}
                    <TableCell>{s.causeForLeaving}</TableCell>
                    <TableCell>{s.remarks}</TableCell>

                    <TableCell className="no-print">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => openEditDialog(s)}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {displayedStudents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={17} align="center" sx={{ py: 3 }}>
                      No students found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </DataTable>
          <TablePagination
            className="no-print"
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[15, 25, 50, 100, 500]}
          />
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={Boolean(editingStudent)} onClose={closeEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Update Register Entry: {editingStudent?.user?.name}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Name & Admission Number */}
            <Grid size={{ xs: 12, sm: 4 }}   >
              <TextField fullWidth label="Surname" name="surname" value={formData.surname || ''} onChange={handleChange} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}   >
              <TextField fullWidth label="Other Name(s)" name="otherNames" value={formData.otherNames || ''} onChange={handleChange} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}   >
              <TextField fullWidth label="Admission Number" name="admissionNumber" value={formData.admissionNumber || ''} onChange={handleChange} />
            </Grid>

            {/* Admission */}
            <Grid size={{ xs: 12, sm: 6 }}   >
              <TextField fullWidth type="date" label="Date of Admission" InputLabelProps={{ shrink: true }} name="dateOfAdmission" value={formData.dateOfAdmission || ''} onChange={handleChange} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}   >
              <TextField fullWidth label="Last School Attended" name="lastSchoolAttended" value={formData.lastSchoolAttended || ''} onChange={handleChange} />
            </Grid>

            {/* Withdrawal */}
            <Grid size={{ xs: 12, sm: 6 }}   >
              <TextField fullWidth type="date" label="Date of Withdrawal" InputLabelProps={{ shrink: true }} name="dateOfLeaving" value={formData.dateOfLeaving || ''} onChange={handleChange} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}   >
              <TextField fullWidth label="Cause for Leaving" name="causeForLeaving" value={formData.causeForLeaving || ''} onChange={handleChange} />
            </Grid>

            {/* Remarks */}
            <Grid size={{ xs: 12 }}  >
              <TextField fullWidth multiline rows={2} label="Remarks" name="remarks" value={formData.remarks || ''} onChange={handleChange} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Numbers Dialog */}
      <Dialog open={generateDialogOpen} onClose={() => !isGenerating && setGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Admission Numbers</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            You are about to generate sequential admission numbers for <strong>{studentsToGenerate.length}</strong> students.
          </Typography>
          <Typography color="text.secondary" gutterBottom sx={{ mb: 3 }}>
            The system will automatically detect the prefix, numeric sequence, and suffix from your input below and increment the number for each student.
          </Typography>
          <TextField
            fullWidth
            label="Starting Admission Number"
            variant="outlined"
            value={generatePattern}
            onChange={(e) => setGeneratePattern(e.target.value)}
            disabled={isGenerating}
            placeholder="e.g. STU-0001 or 2024A-01X"
            helperText="Enter the first number to use. Example: STU-0001"
          />
          {isGenerating && generateProgress && (
            <Box mt={3} textAlign="center">
              <CircularProgress size={24} sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Generating {generateProgress.current} / {generateProgress.total}... Please do not close this window.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)} disabled={isGenerating}>Cancel</Button>
          <Button variant="contained" onClick={handleGenerateNumbers} disabled={isGenerating || !generatePattern.trim()}>
            {isGenerating ? 'Generating...' : 'Start Generation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}